import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { PublicRoute } from './components/PublicRoute';
import { ProtectedRoute } from './components/ProtectedRoute';
import { LoginPage } from './pages/LoginPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ROUTES } from './utils/navigationUtils';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { TimezoneProvider } from './contexts/TimezoneContext';
import { UploadProvider, useUpload } from './contexts/UploadContext';
import { MultiUploadProgressIndicator } from './components/MultiUploadProgressIndicator';
import { AdminDashboard } from './pages/admin';
import { WorkerDashboard } from './pages/worker';
import ManagerDashboard from './pages/manager/Dashboard';
import ManagerChatbotPage from './pages/manager/ChatbotPage';
import PastChats from './pages/manager/PastChats';

import { ManagerLayout } from './layouts/ManagerLayout';
import { AdminLayout } from './layouts/AdminLayout';
import AdminManageSop from './pages/admin/ManageSop';
import { AdminUsers } from './pages/admin/Users';
import { AdminReports } from './pages/admin/Reports';
import { AdminFeedback } from './pages/admin/Feedback';
import AdminSettings from './pages/admin/Settings';
import ResetPasswordPage from './components/ResetPasswordPage';
import SetPasswordPage from './components/SetNewPassword';
import './index.scss';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-datepicker/dist/react-datepicker.css';
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer, toast } from "react-toastify";
import { MyWorkers } from './pages/manager/MyWorkers';
import { setGlobalLogoutHandler } from './services/api';

// Component to handle global logout with toast notification
const GlobalLogoutHandler: React.FC = () => {
    const { logout } = useAuth();

    useEffect(() => {
        const handleGlobalLogout = (reason?: string) => {
            // Clear any existing toasts to prevent duplicates
            toast.dismiss();

            if (reason === 'session-timeout') {
                toast.warning('Session expired. Please login again.', {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
            } else if (reason === 'account-deactivated') {
                toast.error('Your account is inactive. Please contact your administrator.', {
                    position: "top-right",
                    autoClose: 7000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
            }
            logout();
        };

        setGlobalLogoutHandler(handleGlobalLogout);

        return () => {
            setGlobalLogoutHandler(() => { });
        };
    }, [logout]);

    return null;
};

// Global upload progress indicator: only visible to admin (only admin can upload SOPs)
const GlobalUploadIndicator: React.FC = () => {
    const { user } = useAuth();
    const { getUploads, closeIndicator } = useUpload();

    if (user?.role !== 'admin') return null;

    const activeUploads = getUploads().filter(upload => upload.showIndicator);
    if (activeUploads.length === 0) return null;

    return (
        <MultiUploadProgressIndicator
            uploads={activeUploads}
            onClose={fileId => closeIndicator(fileId)}
        />
    );
};

function App() {
    return (
        <>
            <AuthProvider>
                <SettingsProvider>
                    <TimezoneProvider>
                        <NotificationProvider>
                            <UploadProvider>
                                <GlobalLogoutHandler />
                                <GlobalUploadIndicator />
                                <Router>
                                    <div className="h-full min-h-0 flex flex-col overflow-hidden w-full">
                                        <main className="flex-1 flex flex-col min-h-0 min-w-0 w-full overflow-hidden">
                                            <Routes>
                                                {/* Redirect home to login */}
                                                <Route path={ROUTES.HOME} element={<Navigate to={ROUTES.LOGIN} replace />} />

                                                <Route path={ROUTES.LOGIN} element={
                                                    <PublicRoute>
                                                        <LoginPage />
                                                    </PublicRoute>
                                                } />

                                                <Route path={ROUTES.FORGOT_PASSWORD} element={
                                                    <PublicRoute>
                                                        <ForgotPasswordPage />
                                                    </PublicRoute>
                                                } />

                                                <Route path={ROUTES.RESET_PASSWORD} element={
                                                    <PublicRoute>
                                                        <ResetPasswordPage />
                                                    </PublicRoute>
                                                } />

                                                <Route path={ROUTES.SET_PASSWORD} element={
                                                    <PublicRoute>
                                                        <SetPasswordPage />
                                                    </PublicRoute>
                                                } />

                                                {/* Admin routes */}
                                                <Route path="/admin" element={
                                                    <ProtectedRoute requiredRole="admin">
                                                        <AdminLayout />
                                                    </ProtectedRoute>
                                                }>
                                                    <Route index element={<AdminDashboard />} />
                                                    <Route path="sop" element={<AdminManageSop />} />
                                                    <Route path="users" element={<AdminUsers />} />
                                                    <Route path="chat" element={<AdminReports />} />
                                                    <Route path="feedback" element={<AdminFeedback />} />
                                                    <Route path="settings" element={<AdminSettings />} />
                                                </Route>

                                                <Route path={ROUTES.WORKER_DASHBOARD} element={
                                                    <ProtectedRoute>
                                                        <WorkerDashboard />
                                                    </ProtectedRoute>
                                                } />

                                                {/* Manager routes */}
                                                <Route path="/manager" element={
                                                    <ProtectedRoute requiredRole="manager">
                                                        <ManagerLayout />
                                                    </ProtectedRoute>
                                                }>
                                                    <Route index element={<ManagerDashboard />} />
                                                    <Route path="chatbot" element={<ManagerChatbotPage />} />
                                                    <Route path="past-chats" element={<PastChats />} />
                                                    <Route path="my-workers" element={<MyWorkers />} />
                                                    <Route path="settings" element={<div className="text-center py-8"><h2 className="text-xl font-semibold">Settings</h2><p className="text-gray-600">Settings page coming soon</p></div>} />
                                                </Route>

                                                {/* Catch-all route for unmatched paths */}
                                                <Route path="*" element={
                                                    <div className="min-h-screen flex items-center justify-center">
                                                        <div className="text-center">
                                                            <h1 className="text-4xl font-bold text-gray-900 mb-4">404 - Page Not Found</h1>
                                                            <p className="text-gray-600">The page you're looking for doesn't exist.</p>
                                                        </div>
                                                    </div>
                                                } />
                                            </Routes>
                                        </main>
                                    </div>
                                </Router>
                            </UploadProvider>
                        </NotificationProvider>
                    </TimezoneProvider>
                </SettingsProvider>
            </AuthProvider>
            <ToastContainer position="top-right" autoClose={3000} />
        </>
    );
}


export default App;
